import { memo, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import ProductList from "app/pages/Listing/ProductList/ProductList.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { sendTagOnMediaViewOffers } from "app/utils/analytics";
import PropTypes from "prop-types";
import { merchandisingShape, merchandisingTopic, productShape } from "app/utils/propTypes";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import "./MerchandisingListing.scss";
import MerchandisingFilters from "app/pages/Merchandising/MerchandisingFilters/MerchandisingFilters";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import Loader from "app/pages/.shared/Loader";
import MerchandisingMedias from "app/pages/Merchandising/Media/MerchandisingMedias";
import { FormattedMessage } from "react-intl";
import Button from "app/pages/.shared/form/Button";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import { scrollToElement } from "app/utils/scroller";
import RelativeLink from "app/pages/.shared/RelativeLink";
import MerchandisingProductsFilterContainer from "app/pages/Merchandising/MerchandisingProductsFilterContainer";
import Paragraphs from "app/pages/.shared/Paragraphs";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import useOnSticky from "app/pages/.shared/useOnSticky";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import MerchListingActionbar from "app/pages/.shared/MerchListingActionbar/MerchListingActionbar";
import SmartDPMarketingHeadlineContainer from "app/pages/SmartDP/SmartDPMarketingHeadline/SmartDPMarketingHeadlineContainer";
import HomeSmartDPSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/HomeSmartDPSearchInput";
import SmartDPSearchFormContainer from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchFormContainer";
import { useFsFlag } from "@flagship.io/react-sdk";
import { buildSDPSearchPayload } from "app/utils/smartDPUtils";
import { useNavigate } from "react-router-dom";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import MerchandisingListingHeadMetadata from "app/pages/Merchandising/MerchandisingListing/MerchandisingListingHeadMetadata";

const IMG_DESKTOP_SIZES = {
	medium: { height: 250, width: 1024 },
	large: { height: 250, width: 1024 },
	xlarge: { height: 250, width: 1024 },
};

const IMG_SIZES = {
	medium: { width: 978 },
	small: { width: 768 },
	large: { width: 1847 },
	xlarge: { width: 1847 },
};
const MEDIA_IMG_DESKTOP_SIZES = {
	medium: { width: 1440 },
	large: { width: 1440 },
	xlarge: { width: 1440 },
};

const IMG_MOBILE_SIZES = {
	small: {},
	medium: {},
};

const MerchandisingListing = ({
	productsOfMerchandising = [],
	resolution,
	currentMerchandising = {},
	merchandisingTopics = [],
	merchandisingMedias = [],
	activeFilter,
	fallbackRedirectUrl,
	loading,
	showSmartDPOnRootUrl,
	listingSEO = {},
	resetAllSDPProductsFilter = () => {},
}) => {
	const navigate = useNavigate();
	const productListRef = useRef();
	const actionBarRef = useRef();
	const filtersRef = useRef();
	const showMiniSDPFormFlag = useFsFlag("sdp_show_mini_form_mobile", false);
	const showMiniSDPForm = showMiniSDPFormFlag.getValue();
	const isElementOnScreen = useOnSticky({ ref: filtersRef });

	const { track } = useAnalytics();

	const isLargeResolution = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const currentMerchandisingFooter = currentMerchandising.footer || {};
	const {
		activateRedirect = false,
		labelRedirectListing = <FormattedMessage id="merchandising.footer.redirect.cta.text" />,
		positionRedirectListing = "center",
	} = currentMerchandisingFooter;

	const handleFormSubmission = useCallback(data => {
		const queryParams = buildSDPSearchPayload(data);

		resetAllSDPProductsFilter();

		navigate({ pathname: "/sdp/listing", search: queryParams });
	}, []);

	const buildMerchandisingPicture = useCallback(
		merchandisingTopic => {
			if (merchandisingTopic) {
				return {
					merchandisingPictureUrl: isLargeResolution
						? merchandisingTopic.photoLarge?.url
						: merchandisingTopic.photoSmall?.url,
					merchandisingPictureThumbnail: isLargeResolution
						? merchandisingTopic.photoLarge?.thumbnail
						: merchandisingTopic.photoSmall?.thumbnail,
				};
			}
			return {
				merchandisingPictureUrl: isLargeResolution
					? currentMerchandising.mainPhotoLarge?.url
					: currentMerchandising.mainPhotoSmall?.url,
				merchandisingPictureThumbnail: isLargeResolution
					? currentMerchandising.mainPhotoLarge?.thumbnail
					: currentMerchandising.mainPhotoSmall?.thumbnail,
			};
		},
		[isLargeResolution, currentMerchandising]
	);

	const merchandisingTopic = merchandisingTopics.find(
		merchandisingTopic => activeFilter === merchandisingTopic.filterValue
	);

	const merchandisingPictureData = buildMerchandisingPicture(merchandisingTopic);

	const [merchandisingPictureUrl, setMerchandisingPictureUrl] = useState(
		merchandisingPictureData.merchandisingPictureUrl
	);
	const [merchandisingPictureThumbnail, setMerchandisingPictureThumbnail] = useState(
		merchandisingPictureData.merchandisingPictureThumbnail
	);

	const handleScrollTopButtonClick = () => {
		window.scrollTo(0, 0);
	};

	const updateMerchandisingMainImage = merchandisingTopic => {
		const merchandisingPictureData = buildMerchandisingPicture(merchandisingTopic);
		setMerchandisingPictureUrl(merchandisingPictureData.merchandisingPictureUrl);
		setMerchandisingPictureThumbnail(merchandisingPictureData.merchandisingPictureThumbnail);
	};

	useEffect(() => {
		const merchandisingPictureData = buildMerchandisingPicture();
		setMerchandisingPictureUrl(merchandisingPictureData.merchandisingPictureUrl);
		setMerchandisingPictureThumbnail(merchandisingPictureData.merchandisingPictureThumbnail);
	}, [currentMerchandising, resolution]);

	const scrollToProducts = useCallback(() => {
		scrollToElement({
			element: productListRef.current,
			offset: isMobile ? -180 : -150,
			disableSmoothScroll: false,
		});
		sendTagOnMediaViewOffers();
	}, [productListRef.current]);

	const hasMedia = currentMerchandising.media;

	const mainPictureClassNames = classNames({
		"listing__merchandising-picture": true,
		"listing__merchandising-picture--media": hasMedia,
	});

	let isMerchandisingExpired = false;

	if (currentMerchandising.endAt) {
		const merchEndDate = new Date(currentMerchandising.endAt);
		const today = new Date();
		isMerchandisingExpired = today > merchEndDate;
	}

	const smartDP = currentMerchandising?.displayOptions?.smartDP;

	if (isEmpty(currentMerchandising)) {
		return (
			<div className="listing listing--merchandising" data-testid="listing-merchandising">
				<div className="listing__loader">
					<Loader />
				</div>
			</div>
		);
	} else if (isMerchandisingExpired) {
		return (
			<div className="listing listing--merchandising" data-testid="listing-merchandising">
				<MerchListingActionbar label={currentMerchandising.label} />

				<div className="listing__container">
					<div className="listing__merchandising-expired">
						<div className="listing__merchandising-expired-message">
							<FormattedMessage id="merchandising.expired.message" />
						</div>
						<RelativeLink
							to={{ pathname: fallbackRedirectUrl }}
							variant="primary"
							enableLoading
						>
							<FormattedMessage id="confirmation.leave.button" />
						</RelativeLink>
					</div>
				</div>
			</div>
		);
	}

	const listingMerchandisingClassName = classNames({
		"listing--merchandising": true,
		"listing--merchandising__media": hasMedia,
	});
	const smartDPSearchClassName = classNames({
		"smartdp-search__content": true,
		"smartdp-search__content-without-sdp-form": smartDP?.hideSmartDPForm,
		"smartdp-search__content-with-sdp-form": !smartDP?.hideSmartDPForm && !showMiniSDPForm,
		"smartdp-search__content-with-sdp-form-mini-version":
			!smartDP?.hideSmartDPForm && showMiniSDPForm,
	});

	const smartDPFormClassName = classNames({
		"smartdp-search__form": true,
		"smartdp-search__form-mini-version": showMiniSDPForm,
	});
	const smartDPContainerClassName = classNames({
		"smartdp-search__container": true,
		"smartdp-search__container-with-sdp-form-mini-version":
			!smartDP?.hideSmartDPForm && showMiniSDPForm,
	});

	return (
		<div className={listingMerchandisingClassName} data-testid="listing-merchandising">
			<MerchandisingListingHeadMetadata merchandisingLabel={currentMerchandising.label} />

			<>
				{merchandisingPictureUrl && !smartDP?.enabled && (
					<div className={mainPictureClassNames} data-testid="main-picture">
						<div className="listing__container">
							{isLargeResolution ? (
								<ProgressiveImg
									sizes={hasMedia ? MEDIA_IMG_DESKTOP_SIZES : IMG_DESKTOP_SIZES}
									src={merchandisingPictureUrl}
									thumbnail={merchandisingPictureThumbnail}
								/>
							) : (
								<ProgressiveImg
									sizes={IMG_MOBILE_SIZES}
									src={merchandisingPictureUrl}
									thumbnail={merchandisingPictureThumbnail}
								/>
							)}

							{hasMedia && (
								<div className="listing__merchandising-cta">
									<Button submit={false} onClick={scrollToProducts}>
										<FormattedMessage id="merchandising.cta.text" />
										<IconArrowDown
											height={10}
											width={10}
											style={{ marginLeft: 10 }}
										/>
									</Button>
								</div>
							)}
						</div>
					</div>
				)}

				{smartDP?.enabled && (
					<div className={smartDPContainerClassName}>
						<div className="smartdp-search__background-image">
							{hasMedia && !isLargeResolution ? (
								<ProgressiveImg
									sizes={IMG_MOBILE_SIZES}
									thumbnail={merchandisingPictureThumbnail}
									src={merchandisingPictureUrl}
								/>
							) : (
								<BackgroundProgressiveImg
									sizes={IMG_SIZES}
									resolution={resolution}
									backgroundPosition={"center"}
									thumbnail={merchandisingPictureThumbnail}
									src={merchandisingPictureUrl}
								/>
							)}
						</div>
						<div className={smartDPSearchClassName}>
							<SmartDPMarketingHeadlineContainer
								heroTitleDesktop={smartDP?.merchandisingTitleDesktop}
								heroTitleMobile={smartDP?.merchandisingTitleMobile}
								heroSubtitleDesktop={smartDP?.merchandisingSubtitleDesktop}
								heroSubtitleMobile={smartDP?.merchandisingSubtitleMobile}
								heroTextAlign={smartDP?.heroTextAlign}
							/>
							{!smartDP?.hideSmartDPForm && (
								<div className={smartDPFormClassName}>
									{showMiniSDPForm && isMobile ? (
										<HomeSmartDPSearchInput
											onSuccess={handleFormSubmission}
											showMiniSDPForm
										/>
									) : (
										<SmartDPSearchFormContainer
											onSuccess={handleFormSubmission}
										/>
									)}
								</div>
							)}
						</div>
					</div>
				)}

				{hasMedia && (
					<div className="listing__merchandising-media">
						<MerchandisingMedias
							resolution={resolution}
							articles={merchandisingMedias}
						/>
					</div>
				)}

				<div className="listing__merchandising-header" ref={actionBarRef}>
					<MerchListingActionbar
						/**
						 * on affiche le nombre de produits que
						 * lorsque la requête côté front est finie
						 */
						count={!loading && productsOfMerchandising.length}
						label={currentMerchandising?.label}
						fromPrice={currentMerchandising?.lowestPrice}
					/>
				</div>

				{get(currentMerchandising, "displayOptions.showFilters") && (
					<MerchandisingProductsFilterContainer
						className="listing__merchandising-filters"
						asideFilterMenu={true}
						filtersRef={filtersRef}
						resolution={resolution}
						isStuck={isElementOnScreen}
						scrollToListingHeader={() => {
							scrollToElement({
								element: actionBarRef.current,
								offset: 0,
								disableSmoothScroll: true,
							});
						}}
					/>
				)}

				<div className="listing__merchandising-products" ref={productListRef}>
					<div className="listing__container">
						{currentMerchandising?.displayOptions?.introduction && (
							<div
								className="listing__introduction"
								data-testid="listing-introduction"
							>
								<Paragraphs
									paragraphs={currentMerchandising.displayOptions.introduction}
									enableHTMLFormatting
								/>
							</div>
						)}

						{merchandisingTopics.length > 0 && (
							<div className="listing__merchandising-filters">
								<MerchandisingFilters
									activeFilter={activeFilter}
									merchandisingTopics={merchandisingTopics}
									onFilterSelect={updateMerchandisingMainImage}
								/>
							</div>
						)}

						<div className="listing__product-list">
							<ProductList
								products={productsOfMerchandising}
								showSmartDPOnRootUrl={showSmartDPOnRootUrl}
							/>
						</div>
						{Boolean(activateRedirect) && (
							<div className="listing__redirect" data-testid="listing-redirect">
								<RelativeLink
									className={`button button--secondary listing__redirect-cta listing__redirect-cta--${positionRedirectListing}`}
									data-testid="listing-redirect-cta"
									to={{ pathname: "/listing" }}
									onClick={() => track("merch_listing_redirect_cta_clicked")}
								>
									{labelRedirectListing}
								</RelativeLink>
							</div>
						)}

						{loading && (
							<div className="listing__loader">
								<Loader />
							</div>
						)}
					</div>
				</div>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>
			</>
			<span className="icon scroll-top-button" onClick={handleScrollTopButtonClick}>
				<IconGoToTopArrow width={50} height={50} />
			</span>

			<div className="listing__rtb">
				<div className="listing__container">
					<ReasonToBookBlockContainer />
				</div>
			</div>
			<CookiePolicyFooterContainer />
			<PushNotificationContainer />
		</div>
	);
};

MerchandisingListing.propTypes = {
	loading: PropTypes.bool,
	activeFilter: PropTypes.string,
	resolution: PropTypes.string,
	productsOfMerchandising: PropTypes.arrayOf(productShape),
	merchandisingMedias: PropTypes.arrayOf(merchandisingShape),
	currentMerchandising: merchandisingShape,
	merchandisingTopics: PropTypes.arrayOf(merchandisingTopic),
	fallbackRedirectUrl: PropTypes.string,
	showSmartDPOnRootUrl: PropTypes.bool,
	listingSEO: PropTypes.object,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default memo(MerchandisingListing);
